import React, { useMemo } from 'react';
import dayjs from 'dayjs';
import DataGrid from '../../../../common/DataGrid/DataGrid';
import { useTranslation } from 'react-i18next';
import { useDataGridTextFilter } from '../../../../common/DataGrid/filters/useDataGridTextFilter';
import { useOptionsDataGridFilter } from '../../../../common/DataGrid/filters/DataGridFilters.jsx';
import { IconButton } from '../../../../common/controls/Controls';
import { DownloadOutlined } from '@ant-design/icons';

const PhdIndividualPlansTable = ({
  individualPlans,
  onDetailClick,
  onDelete,
  onDownload,
  canDownloadRow,
  isDownloading = false,
  pagination,
  onPaginationChange,
}) => {
  const { t } = useTranslation();

  const studentFilter = useDataGridTextFilter('student_display_name');
  const thesisFilter = useDataGridTextFilter('thesis_title');
  const updatedByFilter = useDataGridTextFilter('updated_by_name');
  const schoolYearOptions = useMemo(() => {
    const labels = Array.from(
      new Set(
        (individualPlans || [])
          .map((plan) => plan?.school_year_label)
          .filter((value) => value),
      ),
    ).sort((a, b) =>
      String(a).localeCompare(String(b), undefined, { sensitivity: 'base' }),
    );

    return labels.reduce((acc, label) => {
      acc[label] = label;
      return acc;
    }, {});
  }, [individualPlans]);
  const schoolYearFilter = useOptionsDataGridFilter(
    'school_year_label',
    schoolYearOptions,
  );

  const columns = [
    {
      title: t('Študent'),
      dataIndex: 'student_display_name',
      key: 'student_display_name',
      ellipsis: true,
      ...studentFilter,
    },
    {
      title: t('Názov témy'),
      dataIndex: 'thesis_title',
      key: 'thesis_title',
      ellipsis: true,
      ...thesisFilter,
    },
    {
      title: t('Školský rok'),
      dataIndex: 'school_year_label',
      key: 'school_year_label',
      ellipsis: true,
      ...schoolYearFilter,
    },
    {
      title: t('Naposledy upravené'),
      dataIndex: 'updated_at',
      key: 'updated_at',
      ellipsis: true,
      render: (value) =>
        value ? dayjs(value).format('DD.MM.YYYY HH:mm') : '—',
    },
    {
      title: t('Upravil'),
      dataIndex: 'updated_by_name',
      key: 'updated_by_name',
      ellipsis: true,
      render: (value) => value || '—',
      ...updatedByFilter,
    },
  ];

  const customSettings = (row) => (
    <IconButton
      icon={<DownloadOutlined />}
      tooltip={t('Stiahnuť DOCX')}
      disabled={Boolean(
        !onDownload || isDownloading || (canDownloadRow && !canDownloadRow(row)),
      )}
      disabledTooltip={
        !onDownload
          ? t('Sťahovanie nie je dostupné')
          : canDownloadRow && !canDownloadRow(row)
            ? t('Archívnu verziu môže vydať iba administrátor')
          : isDownloading
            ? t('Dokument sa práve generuje')
            : undefined
      }
      onClick={(e) => {
        e.stopPropagation();
        if (
          !onDownload ||
          isDownloading ||
          (canDownloadRow && !canDownloadRow(row))
        ) {
          return;
        }
        onDownload(row);
      }}
    />
  );

  return (
    <DataGrid
      locale={{
        emptyText: t(
          'Pre zvolený pohľad a filtre nie sú dostupné žiadne záznamy.',
        ),
      }}
      rowKey={(plan) => plan.id}
      columns={columns}
      dataSource={individualPlans || []}
      permissionsBasic={'phd-individual-plans'}
      uniqueTableId={'phd-individual-plans-table'}
      customSettings={customSettings}
      onEditClick={onDetailClick}
      onRowClick={onDetailClick}
      onDelete={onDelete}
      secondaryCondition={(row) => row.can_edit !== false}
      deleteCondition={(row) => row.can_delete !== false}
      editDisabledTooltip={t('Archivovaný záznam je iba na čítanie')}
      deleteDisabledTooltip={t('Archivovaný záznam nie je možné vymazať')}
      pagination={pagination}
      onChange={onPaginationChange}
    />
  );
};

export default PhdIndividualPlansTable;
